import React, {
    ReactElement,
    ReactNode,
    createContext,
    useCallback,
    useMemo,
    useState,
} from "react"

import { LCTOnlyDatasets } from "../../../../../../constants/enums"
import { AppError } from "../../../../../../errors/errorProcessors"
import usePublishLCTDevice from "../../../../../../services/middleware/hooks/devices/lct/usePublishLCTDevice"
import useSaveLCTDeviceDraft from "../../../../../../services/middleware/hooks/devices/lct/useSaveLCTDeviceDraft"
import { checkContextErrors } from "../../../../../../utils/registerDevice/registerDeviceUtils"
import { IDeviceFormContextValue } from "../../../../types/providerTypes"
import { EVDCDeviceFormData } from "../../../types/providerTypes"

type CreateEVDCDeviceProviderProps = {
    formData: EVDCDeviceFormData
    setFormData: React.Dispatch<React.SetStateAction<EVDCDeviceFormData>>
    formDataIsLoading: boolean
    formDataError?: AppError
    systemReference?: string
    attachmentUUID?: string
    canEditManufacturer: boolean
    children: ReactNode
}

export const CreateEVDCDeviceContext = createContext(
    {} as IDeviceFormContextValue<EVDCDeviceFormData>
)

const CreateEVDCDeviceProvider = ({
    formData,
    setFormData,
    formDataIsLoading,
    systemReference,
    formDataError,
    attachmentUUID,
    canEditManufacturer,
    children,
}: CreateEVDCDeviceProviderProps): ReactElement => {
    const [currentIndex, setCurrentIndex] = useState<number>(0)
    const [deviceRef, setDeviceRef] = useState<string | undefined>()
    const [errors, setErrors] = useState<AppError[]>([])

    const {
        publishLCTDevice,
        error: publishError,
        isLoaded: publishSuccessful,
        isLoading: publishInProgress,
    } = usePublishLCTDevice(
        formData,
        LCTOnlyDatasets.EVDC,
        attachmentUUID,
        systemReference
    )

    const {
        saveLCTDeviceDraft,
        isLoading: saveDraftInProgress,
        isLoaded: saveDraftSuccessful,
    } = useSaveLCTDeviceDraft<EVDCDeviceFormData>(
        false,
        LCTOnlyDatasets.EVDC,
        systemReference,
        attachmentUUID
    )

    checkContextErrors([formDataError, publishError], errors, (error) =>
        setErrors((prev) => [...prev, error])
    )
    const setIndex = (step: number): void => {
        setErrors([])
        setCurrentIndex(step)
    }

    const onSubmit = useCallback(async () => {
        const res = await publishLCTDevice()
        setDeviceRef(res?.deviceRef)
    }, [publishLCTDevice, setDeviceRef])

    const value: IDeviceFormContextValue<EVDCDeviceFormData> = useMemo(
        () => ({
            formData,
            setFormData,
            currentIndex,
            setCurrentIndex: setIndex,
            onSubmit,
            submitLoading: publishInProgress,
            submitSuccessful: publishSuccessful,
            canEditManufacturer,
            formIsLoading: formDataIsLoading,
            saveDraft: saveLCTDeviceDraft,
            saveDraftLoading: saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        }),
        [
            formData,
            setFormData,
            currentIndex,
            onSubmit,
            publishInProgress,
            publishSuccessful,
            canEditManufacturer,
            formDataIsLoading,
            saveLCTDeviceDraft,
            saveDraftInProgress,
            saveDraftSuccessful,
            deviceRef,
            attachmentUUID,
            errors,
        ]
    )

    return (
        <CreateEVDCDeviceContext.Provider value={value}>
            {children}
        </CreateEVDCDeviceContext.Provider>
    )
}

CreateEVDCDeviceProvider.defaultProps = {
    formDataError: undefined,
    attachmentUUID: undefined,
    systemReference: undefined,
}

export default CreateEVDCDeviceProvider
